import React, { useEffect, useState } from "react"
import { useParams, useHistory, useLocation } from "react-router-dom"
import {
  ThemeProvider,
  CssBaseline,
  Container,
  Paper,
  Typography,
  Grid,
  Box,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core"
import {
  getPlanById,
  nationalCorporation,
  regionalCorporation,
  elitePremium,
  elitPlus,
  elite,
  solopreneur,
  professional,
  investorsWealthManagementPlatinum,
  investorsWealthManagementGold,
  investorsWealthManagementSilver,
  b2tMember1,
  b2tMember2,
  b2tMember3,
} from "../SignUpPaywallPages/utils/subscriptionConfig"
import { createMuiTheme } from "@material-ui/core/styles"
import { useSelector } from "react-redux"
import services from "../../services"
import ErrorDialog from "../../components/ErrorDialog"
import styles from "./UserApplicationPage.module.scss"
import { ADMIN } from "../../helpers/userRoles"
import { isMarkerIOUserAllowed } from "../../services/markerIOService"

const UserApplicationPage = () => {
  const { id } = useParams()
  const history = useHistory()
  const location = useLocation()
  const token = new URLSearchParams(location.search).get("token")
  const applicationId = id || token
  const isViewOnlyAccess =
    Boolean(token) || /^[a-f0-9]{20}$/i.test(id || "")
  const [application, setApplication] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const userInfo = useSelector((state) => state.user)
  const isAdmin = userInfo?.role === ADMIN
  const showAdminControls = isAdmin && !isViewOnlyAccess

  useEffect(() => {
    const fetchApplication = async () => {
      if (!applicationId) {
        setError("No application ID provided")
        setLoading(false)
        return
      }

      try {
        const response =
          await services.userApplicationServices.getUserApplication(applicationId)
        if (response && response.data) {
          setApplication(response.data)
        } else {
          setError("Application data not found")
        }
      } catch (error) {
        console.error("Error fetching application:", error)
        if (error.response?.status === 401) {
          setError({
            title: "Access Denied",
            message:
              "This application is not accessible. If you believe this is an error, please contact our customer support team.",
            support: true,
          })
        } else {
          setError({
            title: "Error Loading Application",
            message:
              "We're unable to load the application details at this time. Please try again later or contact our customer support team if the issue persists.",
            support: true,
          })
        }
      } finally {
        setLoading(false)
      }
    }

    fetchApplication()
  }, [applicationId])

  const handleAccept = async () => {
    // For provider + other, we need to select a plan first
    if (
      application.business_type === "company_provider" &&
      application.business_category === "other"
    ) {
      setOpenPlanDialog(true)
      return
    }

    // For all other applications, let the backend calculate the plan
    try {
      await services.userApplicationServices.acceptApplication(id)
      // Refresh the page to show updated status
      window.location.reload()
    } catch (error) {
      setError({
        title: "Error Accepting Application",
        message:
          "Failed to accept the application. Please try again or contact support if the issue persists.",
        support: true,
      })
    }
  }

  const handlePlanDialogOpen = () => {
    setOpenPlanDialog(true)
  }

  const handlePlanDialogClose = () => {
    setOpenPlanDialog(false)
  }

  const handlePlanSelect = (event) => {
    setSelectedPlan(event.target.value)
  }

  const handlePlanConfirm = async () => {
    try {
      await services.userApplicationServices.acceptApplication(id, selectedPlan)
      // Refresh the page to show updated status
      window.location.reload()
    } catch (error) {
      setError({
        title: "Error Accepting Application",
        message:
          "Failed to accept the application. Please try again or contact support if the issue persists.",
        support: true,
      })
    } finally {
      handlePlanDialogClose()
    }
  }

  const handleReject = async () => {
    try {
      await services.userApplicationServices.rejectApplication(id)
      // Refresh the page to show updated status
      window.location.reload()
    } catch (error) {
      setError({
        title: "Error Declining Application",
        message:
          "Failed to decline the application. Please try again or contact support if the issue persists.",
        support: true,
      })
    }
  }

  const [requestMessage, setRequestMessage] = useState("")
  const [openRequestDialog, setOpenRequestDialog] = useState(false)

  // State for subscription plan selection
  const [openPlanDialog, setOpenPlanDialog] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState("")

  // All available subscription plans
  const subscriptionPlans = [
    nationalCorporation,
    regionalCorporation,
    elitePremium,
    elitPlus,
    elite,
    solopreneur,
    professional,
    investorsWealthManagementPlatinum,
    investorsWealthManagementGold,
    investorsWealthManagementSilver,
    b2tMember1,
    b2tMember2,
    b2tMember3,
  ]

  // Set empty plan only for company_provider with category 'other'
  // All other plan calculations are done on the backend
  useEffect(() => {
    if (application) {
      if (
        application.business_type === "company_provider" &&
        application.business_category === "other"
      ) {
        // For provider + other, don't set a default plan
        setSelectedPlan("")
      } else {
        // For all other types, we don't need to set a plan as it will be calculated on the backend
        setSelectedPlan("")
      }
    }
  }, [application])

  const handleRequestDetailsOpen = () => {
    setOpenRequestDialog(true)
  }

  const handleRequestDetailsClose = () => {
    setOpenRequestDialog(false)
    setRequestMessage("")
  }

  const handleRequestDetailsSend = async () => {
    try {
      await services.userApplicationServices.requestDetails(id, requestMessage)
      // Refresh the page to show updated status
      window.location.reload()
    } catch (error) {
      setError({
        title: "Error Requesting Details",
        message:
          "Failed to request additional details. Please try again or contact support if the issue persists.",
        support: true,
      })
    } finally {
      handleRequestDetailsClose()
    }
  }

  const handleErrorClose = () => {
    setError(null)
  }

  const handleContactSupport = async () => {
    try {
      const { openMarkerIO } = await import("../../services/markerIOService")
      await openMarkerIO(userInfo)
    } catch (error) {
      console.error("Failed to open Marker.io widget:", error)
    }
  }

  const darkTheme = createMuiTheme({
    palette: {
      type: "dark",
    },
  })

  if (loading) {
    return (
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <div className={styles.container}>
          <div className={styles.content}>
            <Paper className={styles.paper}>
              <Typography variant="h6">Loading...</Typography>
            </Paper>
          </div>
        </div>
      </ThemeProvider>
    )
  }

  if (error) {
    return (
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <div className={styles.container}>
          <div className={styles.content}>
            <Paper className={styles.paper}>
              <Typography variant="h5" gutterBottom>
                {error.title}
              </Typography>
              <Typography paragraph>{error.message}</Typography>
              {error.support && isMarkerIOUserAllowed(userInfo) && (
                <Box mt={3}>
                  <Button
                    buttonStyle="contained"
                    onClick={handleContactSupport}
                  >
                    Report Issue
                  </Button>
                </Box>
              )}
            </Paper>
          </div>
        </div>
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <div className={styles.container}>
        <div className={styles.content}>
          <Paper className={styles.paper}>
            {application ? (
              <>
                <Typography variant="h4" gutterBottom>
                  Application Details
                </Typography>

                <Grid container spacing={3}>
                  {/* Personal Information */}
                  <Grid item xs={12}>
                    <div className={styles.section}>
                      <Typography variant="h6" className={styles.sectionTitle}>
                        Personal Information
                      </Typography>
                      <div className={styles.field}>
                        <span className={styles.fieldLabel}>Name:</span>
                        {application.first_name} {application.last_name}
                      </div>
                      <div className={styles.field}>
                        <span className={styles.fieldLabel}>Email:</span>
                        {application.email}
                      </div>
                      <div className={styles.field}>
                        <span className={styles.fieldLabel}>Phone:</span>
                        {application.phone_number}
                      </div>
                      {application.title && (
                        <div className={styles.field}>
                          <span className={styles.fieldLabel}>Title:</span>
                          {application.title}
                        </div>
                      )}
                      {application.linkedin_profile && (
                        <div className={styles.field}>
                          <span className={styles.fieldLabel}>
                            LinkedIn Profile:
                          </span>
                          <a
                            href={application.linkedin_profile}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {application.linkedin_profile}
                          </a>
                        </div>
                      )}
                    </div>
                  </Grid>

                  {/* Company Information */}
                  <Grid item xs={12}>
                    <div className={styles.section}>
                      <Typography variant="h6" className={styles.sectionTitle}>
                        Company Information
                      </Typography>
                      <div className={styles.field}>
                        <span className={styles.fieldLabel}>
                          Business Type:
                        </span>
                        {application.business_type}
                      </div>
                      <div className={styles.field}>
                        <span className={styles.fieldLabel}>
                          Business Category:
                        </span>
                        {application.business_category}
                      </div>
                      {application.company_name && (
                        <div className={styles.field}>
                          <span className={styles.fieldLabel}>
                            Company Name:
                          </span>
                          {application.company_name}
                        </div>
                      )}
                      {application.company_phone_number && (
                        <div className={styles.field}>
                          <span className={styles.fieldLabel}>
                            Company Phone:
                          </span>
                          {application.company_phone_number}
                        </div>
                      )}
                      {application.organization_name && (
                        <div className={styles.field}>
                          <span className={styles.fieldLabel}>
                            Organization Name:
                          </span>
                          {application.organization_name}
                        </div>
                      )}
                      {application.organization_phone && (
                        <div className={styles.field}>
                          <span className={styles.fieldLabel}>
                            Organization Phone:
                          </span>
                          {application.organization_phone}
                        </div>
                      )}
                      {application.website && (
                        <div className={styles.field}>
                          <span className={styles.fieldLabel}>Website:</span>
                          <a
                            href={application.website}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {application.website}
                          </a>
                        </div>
                      )}
                      {application.headquarters_country && (
                        <div className={styles.field}>
                          <span className={styles.fieldLabel}>
                            Headquarters Country:
                          </span>
                          {application.headquarters_country}
                        </div>
                      )}
                      {application.is_solopreneur !== undefined && (
                        <div className={styles.field}>
                          <span className={styles.fieldLabel}>
                            Solopreneur:
                          </span>
                          {application.is_solopreneur ? "Yes" : "No"}
                        </div>
                      )}
                    </div>
                  </Grid>

                  {/* Address Information */}
                  <Grid item xs={12}>
                    <div className={styles.section}>
                      <Typography variant="h6" className={styles.sectionTitle}>
                        Address Information
                      </Typography>
                      <div className={styles.field}>
                        <span className={styles.fieldLabel}>
                          Street Address:
                        </span>
                        {application.street_address}
                      </div>
                      {application.address_line2 && (
                        <div className={styles.field}>
                          <span className={styles.fieldLabel}>
                            Address Line 2:
                          </span>
                          {application.address_line2}
                        </div>
                      )}
                      {application.city && (
                        <div className={styles.field}>
                          <span className={styles.fieldLabel}>City:</span>
                          {application.city}
                        </div>
                      )}
                      <div className={styles.field}>
                        <span className={styles.fieldLabel}>State/Region:</span>
                        {application.state_region}
                      </div>
                      <div className={styles.field}>
                        <span className={styles.fieldLabel}>ZIP Code:</span>
                        {application.zip_code}
                      </div>
                      <div className={styles.field}>
                        <span className={styles.fieldLabel}>Country:</span>
                        {application.country}
                      </div>
                    </div>
                  </Grid>

                  {/* Professional Information */}
                  {(application.area_of_specialization ||
                    application.professional_level) && (
                    <Grid item xs={12}>
                      <div className={styles.section}>
                        <Typography
                          variant="h6"
                          className={styles.sectionTitle}
                        >
                          Professional Information
                        </Typography>
                        {application.area_of_specialization && (
                          <div className={styles.field}>
                            <span className={styles.fieldLabel}>
                              Area of Specialization:
                            </span>
                            {application.area_of_specialization}
                          </div>
                        )}
                        {application.professional_level && (
                          <div className={styles.field}>
                            <span className={styles.fieldLabel}>
                              Professional Level:
                            </span>
                            {application.professional_level}
                          </div>
                        )}
                        {application.provides_services_in_more_than_three_states !==
                          undefined && (
                          <div className={styles.field}>
                            <span className={styles.fieldLabel}>
                              Provides Services in More Than 3 States:
                            </span>
                            {application.provides_services_in_more_than_three_states
                              ? "Yes"
                              : "No"}
                          </div>
                        )}
                      </div>
                    </Grid>
                  )}

                  {/* Financial Services Information */}
                  {(application.financial_services_type ||
                    application.funding_opportunities ||
                    application.industry_specialization ||
                    application.target_company_qualifications ||
                    application.revenue_range_aum) && (
                    <Grid item xs={12}>
                      <div className={styles.section}>
                        <Typography
                          variant="h6"
                          className={styles.sectionTitle}
                        >
                          Financial Services Information
                        </Typography>
                        {application.financial_services_type && (
                          <div className={styles.field}>
                            <span className={styles.fieldLabel}>
                              Financial Services Type:
                            </span>
                            {application.financial_services_type}
                          </div>
                        )}
                        {application.funding_opportunities && (
                          <div className={styles.field}>
                            <span className={styles.fieldLabel}>
                              Funding Opportunities:
                            </span>
                            {application.funding_opportunities}
                          </div>
                        )}
                        {application.industry_specialization && (
                          <div className={styles.field}>
                            <span className={styles.fieldLabel}>
                              Industry Specialization:
                            </span>
                            {application.industry_specialization}
                          </div>
                        )}
                        {application.target_company_qualifications && (
                          <div className={styles.field}>
                            <span className={styles.fieldLabel}>
                              Target Company Qualifications:
                            </span>
                            {application.target_company_qualifications}
                          </div>
                        )}
                        {application.revenue_range_aum && (
                          <div className={styles.field}>
                            <span className={styles.fieldLabel}>
                              Revenue Range/AUM:
                            </span>
                            {application.revenue_range_aum}
                          </div>
                        )}
                      </div>
                    </Grid>
                  )}

                  {/* Business Details */}
                  <Grid item xs={12}>
                    <div className={styles.section}>
                      <Typography variant="h6" className={styles.sectionTitle}>
                        Business Details
                      </Typography>
                      <div className={styles.field}>
                        <span className={styles.fieldLabel}>Industries:</span>
                        {application.industries}
                      </div>
                      <div className={styles.field}>
                        <span className={styles.fieldLabel}>Services:</span>
                        {application.services}
                      </div>
                      <div className={styles.field}>
                        <span className={styles.fieldLabel}>
                          Revenue Range:
                        </span>
                        {application.revenue_range}
                      </div>
                      {application.primary_business && (
                        <div className={styles.field}>
                          <span className={styles.fieldLabel}>
                            Primary Business:
                          </span>
                          {application.primary_business}
                        </div>
                      )}
                    </div>
                  </Grid>

                  {/* Diversity & Business Preferences */}
                  <Grid item xs={12}>
                    <div className={styles.section}>
                      <Typography variant="h6" className={styles.sectionTitle}>
                        Diversity Information
                      </Typography>
                      <div className={styles.field}>
                        <span className={styles.fieldLabel}>
                          {application.business_type === "company_provider" &&
                          application.business_category === "corporation"
                            ? "Small & Emerging Business or Supplier:"
                            : "Diverse Business:"}
                        </span>
                        {application.is_diverse_business ? "Yes" : "No"}
                      </div>
                      <div className={styles.field}>
                        <span className={styles.fieldLabel}>
                          {application.business_type === "company_provider" &&
                          application.business_category === "corporation"
                            ? "Small & Emerging Business or Supplier Led Company:"
                            : "Diverse Led:"}
                        </span>
                        {application.is_diverse_led ? "Yes" : "No"}
                      </div>
                      <div className={styles.field}>
                        <span className={styles.fieldLabel}>
                          Top Customers:
                        </span>
                        {application.top_customers}
                      </div>
                      {application.diversity_designations && (
                        <div className={styles.field}>
                          <span className={styles.fieldLabel}>
                            Diversity Designations:
                          </span>
                          {application.diversity_designations}
                        </div>
                      )}
                    </div>
                  </Grid>

                  {/* Business Preferences */}
                  <Grid item xs={12}>
                    <div className={styles.section}>
                      <Typography variant="h6" className={styles.sectionTitle}>
                        Business Preferences
                      </Typography>
                      <div className={styles.preferencesGrid}>
                        <div className={styles.field}>
                          <span className={styles.fieldLabel}>
                            Asian Owned:
                          </span>
                          {application.asian_owned ? "Yes" : "No"}
                        </div>
                        <div className={styles.field}>
                          <span className={styles.fieldLabel}>
                            Black Owned:
                          </span>
                          {application.black_owned ? "Yes" : "No"}
                        </div>
                        <div className={styles.field}>
                          <span className={styles.fieldLabel}>
                            Disabled Owned:
                          </span>
                          {application.disabled_owned ? "Yes" : "No"}
                        </div>
                        <div className={styles.field}>
                          <span className={styles.fieldLabel}>
                            Hispanic Owned:
                          </span>
                          {application.hispanic_owned ? "Yes" : "No"}
                        </div>
                        <div className={styles.field}>
                          <span className={styles.fieldLabel}>LGBTQ:</span>
                          {application.lgbtq ? "Yes" : "No"}
                        </div>
                        <div className={styles.field}>
                          <span className={styles.fieldLabel}>
                            Native American Owned:
                          </span>
                          {application.native_american_owned ? "Yes" : "No"}
                        </div>
                        <div className={styles.field}>
                          <span className={styles.fieldLabel}>
                            Service Disabled Veteran Owned:
                          </span>
                          {application.service_disabled_veteran_owned
                            ? "Yes"
                            : "No"}
                        </div>
                        <div className={styles.field}>
                          <span className={styles.fieldLabel}>
                            Veteran Owned:
                          </span>
                          {application.veteran_owned ? "Yes" : "No"}
                        </div>
                        <div className={styles.field}>
                          <span className={styles.fieldLabel}>
                            Women Owned:
                          </span>
                          {application.women_owned ? "Yes" : "No"}
                        </div>
                      </div>
                    </div>
                  </Grid>

                  {/* Certifications & Qualifications */}
                  {(application.certifications ||
                    application.quality_certifications) && (
                    <Grid item xs={12}>
                      <div className={styles.section}>
                        <Typography
                          variant="h6"
                          className={styles.sectionTitle}
                        >
                          Certifications & Qualifications
                        </Typography>
                        {application.certifications && (
                          <div className={styles.field}>
                            <span className={styles.fieldLabel}>
                              Certifications:
                            </span>
                            <div className={styles.longText}>
                              {application.certifications}
                            </div>
                          </div>
                        )}
                        {application.quality_certifications && (
                          <div className={styles.field}>
                            <span className={styles.fieldLabel}>
                              Quality Certifications:
                            </span>
                            <div className={styles.longText}>
                              {application.quality_certifications}
                            </div>
                          </div>
                        )}
                      </div>
                    </Grid>
                  )}

                  {/* Business Regions */}
                  {application.business_regions && (
                    <Grid item xs={12}>
                      <div className={styles.section}>
                        <Typography
                          variant="h6"
                          className={styles.sectionTitle}
                        >
                          Business Regions
                        </Typography>
                        <div className={styles.field}>
                          <span className={styles.fieldLabel}>
                            Operating Regions:
                          </span>
                          {application.business_regions}
                        </div>
                      </div>
                    </Grid>
                  )}

                  {/* Additional Information */}
                  {(application.additional_info ||
                    application.how_did_you_learn ||
                    application.notes) && (
                    <Grid item xs={12}>
                      <div className={styles.section}>
                        <Typography
                          variant="h6"
                          className={styles.sectionTitle}
                        >
                          Additional Information
                        </Typography>
                        {application.additional_info && (
                          <div className={styles.field}>
                            <span className={styles.fieldLabel}>
                              Additional Information:
                            </span>
                            <div className={styles.longText}>
                              {application.additional_info}
                            </div>
                          </div>
                        )}
                        {application.how_did_you_learn && (
                          <div className={styles.field}>
                            <span className={styles.fieldLabel}>
                              How Did You Learn About Us:
                            </span>
                            <div className={styles.longText}>
                              {application.how_did_you_learn}
                            </div>
                          </div>
                        )}
                        {application.notes && (
                          <div className={styles.field}>
                            <span className={styles.fieldLabel}>Notes:</span>
                            <div className={styles.longText}>
                              {application.notes}
                            </div>
                          </div>
                        )}
                      </div>
                    </Grid>
                  )}

                  {/* Service Fields & Admin Panel */}
                  {showAdminControls && (
                    <Grid item xs={12}>
                      <div className={`${styles.section} ${styles.adminPanel}`}>
                        <Typography
                          variant="h6"
                          className={styles.sectionTitle}
                        >
                          Service Fields & Admin Controls
                        </Typography>
                        <div className={styles.serviceFields}>
                          <div className={styles.field}>
                            <span className={styles.fieldLabel}>
                              Application ID:
                            </span>
                            {application.id}
                          </div>
                          {application.code && (
                            <div className={styles.field}>
                              <span className={styles.fieldLabel}>
                                Application Code:
                              </span>
                              {application.code}
                            </div>
                          )}
                          <div className={styles.field}>
                            <span className={styles.fieldLabel}>Status:</span>
                            <span
                              className={`${styles.status} ${
                                styles[application.status]
                              }`}
                            >
                              {application.status}
                            </span>
                          </div>
                          <div className={styles.field}>
                            <span className={styles.fieldLabel}>
                              Created At:
                            </span>
                            {new Date(application.created_at).toLocaleString()}
                          </div>
                          <div className={styles.field}>
                            <span className={styles.fieldLabel}>
                              Updated At:
                            </span>
                            {new Date(application.updated_at).toLocaleString()}
                          </div>
                          {application.user_id && (
                            <div className={styles.field}>
                              <span className={styles.fieldLabel}>
                                User ID:
                              </span>
                              {application.user_id}
                            </div>
                          )}
                          {application.subscription_plan && (
                            <div className={styles.field}>
                              <span className={styles.fieldLabel}>
                                Subscription Plan:
                              </span>
                              {(() => {
                                try {
                                  const plan = getPlanById(
                                    application.subscription_plan,
                                  )
                                  return (
                                    <div>
                                      <strong>
                                        {plan.title}{" "}
                                        {plan.tier ? `(${plan.tier})` : ""}
                                      </strong>{" "}
                                      - {plan.description}
                                    </div>
                                  )
                                } catch (e) {
                                  return application.subscription_plan
                                }
                              })()}
                            </div>
                          )}
                          {application.request_message && (
                            <div className={styles.field}>
                              <span className={styles.fieldLabel}>
                                Request Message:
                              </span>
                              <div className={styles.requestMessage}>
                                {application.request_message}
                              </div>
                            </div>
                          )}
                          {application.programs_services_upload !==
                            undefined && (
                            <div className={styles.field}>
                              <span className={styles.fieldLabel}>
                                Programs/Services Upload:
                              </span>
                              {application.programs_services_upload
                                ? "Yes"
                                : "No"}
                            </div>
                          )}
                        </div>
                      </div>
                    </Grid>
                  )}

                  {/* Application Status (for non-admin users) */}
                  {!showAdminControls && (
                    <Grid item xs={12}>
                      <div className={styles.section}>
                        <Typography
                          variant="h6"
                          className={styles.sectionTitle}
                        >
                          Application Status
                        </Typography>
                        <div className={styles.field}>
                          <span className={styles.fieldLabel}>Status:</span>
                          <span
                            className={`${styles.status} ${
                              styles[application.status]
                            }`}
                          >
                            {application.status}
                          </span>
                        </div>
                        <div className={styles.field}>
                          <span className={styles.fieldLabel}>Submitted:</span>
                          {new Date(
                            application.created_at,
                          ).toLocaleDateString()}
                        </div>
                        {application.status === "approved" &&
                          application.subscription_plan && (
                            <div className={styles.field}>
                              <span className={styles.fieldLabel}>
                                Subscription Plan:
                              </span>
                              {(() => {
                                try {
                                  const plan = getPlanById(
                                    application.subscription_plan,
                                  )
                                  return (
                                    <div>
                                      <strong>
                                        {plan.title}{" "}
                                        {plan.tier ? `(${plan.tier})` : ""}
                                      </strong>{" "}
                                      - {plan.description}
                                    </div>
                                  )
                                } catch (e) {
                                  return application.subscription_plan
                                }
                              })()}
                            </div>
                          )}
                        {application.status === "request_details" &&
                          application.request_message && (
                            <div className={styles.field}>
                              <span className={styles.fieldLabel}>
                                Request Message:
                              </span>
                              <div className={styles.requestMessage}>
                                {application.request_message}
                              </div>
                              {!isViewOnlyAccess && (
                                <Button
                                  variant="contained"
                                  color="primary"
                                  onClick={() =>
                                    history.push(
                                      `/sign-up-apply/edit/${application.id}`,
                                    )
                                  }
                                  className={styles.editButton}
                                  style={{ marginTop: "16px" }}
                                >
                                  Edit Application
                                </Button>
                              )}
                            </div>
                          )}
                      </div>
                    </Grid>
                  )}
                </Grid>
              </>
            ) : (
              <>
                <Box className={styles.successMessageContainer}>
                  <Typography variant="h5" className={styles.successTitle}>
                    🎉 Congratulations!
                  </Typography>
                  <Typography variant="body1" className={styles.successMessage}>
                    Your application has been submitted successfully.<br /> Our team will review your submission and you will receive a confirmation email shortly with next steps.
                  </Typography>
                  <Typography variant="body2" className={styles.successNote}>
                    Thank you for your interest in joining our platform.<br /> We appreciate your patience during the review process.
                  </Typography>
                </Box>
              </>
            )}

            {showAdminControls && (
              <>
                <Divider className={styles.divider} />
                <div className={styles.buttonContainer}>
                  <Button
                    variant="contained"
                    onClick={handleAccept}
                    disabled={application.status === "approved"}
                    className={`${styles.actionButton} ${styles.accept}`}
                  >
                    Accept
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handlePlanDialogOpen}
                    disabled={application.status === "approved"}
                    className={`${styles.actionButton} ${styles.accept}`}
                    style={{ marginLeft: "8px" }}
                  >
                    Accept with Plan...
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleReject}
                    disabled={application.status === "rejected"}
                    className={`${styles.actionButton} ${styles.reject}`}
                  >
                    Decline
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleRequestDetailsOpen}
                    disabled={application.status === "request_details"}
                    className={`${styles.actionButton} ${styles.requestDetails}`}
                  >
                    Request Details
                  </Button>
                </div>
              </>
            )}
          </Paper>
        </div>
      </div>
      <Dialog
        open={openRequestDialog}
        onClose={handleRequestDetailsClose}
        aria-labelledby="request-details-dialog-title"
      >
        <DialogTitle id="request-details-dialog-title">
          Request Additional Details
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter a message to request additional details from the applicant:
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="request-message"
            label="Request Message"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={requestMessage}
            onChange={(e) => setRequestMessage(e.target.value)}
            InputProps={{
              style: { color: "white" },
            }}
            InputLabelProps={{
              style: { color: "white" },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleRequestDetailsClose}
            style={{ color: "white" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRequestDetailsSend}
            style={{ color: "white" }}
            disabled={!requestMessage.trim()}
          >
            Send Request
          </Button>
        </DialogActions>
      </Dialog>
      <ErrorDialog open={!!error} onClose={handleErrorClose} error={error} />

      {/* Subscription Plan Selection Dialog */}
      <Dialog
        open={openPlanDialog}
        onClose={handlePlanDialogClose}
        aria-labelledby="plan-selection-dialog-title"
      >
        <DialogTitle id="plan-selection-dialog-title">
          Select Subscription Plan
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {application?.business_type === "company_provider" &&
            application?.business_category === "other"
              ? "Please select a subscription plan for this Provider Other application:"
              : "You can change the default subscription plan if needed:"}
          </DialogContentText>
          <FormControl fullWidth margin="dense">
            <InputLabel id="subscription-plan-label">
              Subscription Plan
            </InputLabel>
            <Select
              labelId="subscription-plan-label"
              id="subscription-plan"
              value={selectedPlan}
              onChange={handlePlanSelect}
              fullWidth
            >
              {subscriptionPlans.map((plan) => (
                <MenuItem key={plan.id} value={plan.id}>
                  {plan.title} {plan.tier ? `(${plan.tier})` : ""} -{" "}
                  {plan.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePlanDialogClose} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handlePlanConfirm}
            color="primary"
            disabled={!selectedPlan}
          >
            Approve with Selected Plan
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  )
}

export default UserApplicationPage
